/**
 * LoginSteps — shared photo-as-login flow used by both the `/login` route
 * (full-page chrome) and the in-scene `<LoginModal>` overlay.
 *
 * Phases:
 *   qr       → scan a QR with your phone; the phone POSTs a photo; the desktop
 *              polls a token-keyed status endpoint and is logged in once the
 *              phone finishes. Default landing step; mirrors the
 *              `UploadFlowSteps` QR-first pattern.
 *   idle     → on-device file picker fallback (the "Vyfotit na tomto zařízení"
 *              button on the QR step routes here).
 *   pinging  → /api/auth/photo-login pending (on-device path)
 *   welcome  → "Vítej, Anno!" greeting for ~700 ms, then onComplete(session)
 *
 * Surfaces (route vs. modal) differ only in what happens on success/cancel —
 * the steps themselves are identical.
 *
 * Visual treatment is canonical PoP1 — see wiki `Design-Specs/Photo-Login.md`.
 */
import { useCallback, useEffect, useRef, useState, type ChangeEvent, type DragEvent } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { useQueryClient } from '@tanstack/react-query'
import { QRCodeSVG } from 'qrcode.react'
import { Camera, CheckCircle2, Loader2, RefreshCw, Smartphone } from 'lucide-react'
import {
  queryKeys,
  useCreateLoginSession,
  useLoginSessionStatus,
  usePhotoLogin,
} from '@/hooks/queries'
import type { Session } from '@shared/schemas'

type Phase = 'qr' | 'idle' | 'pinging' | 'welcome'

export type LoginStepsProps = {
  onComplete: (session: Session) => void
  onCancel: () => void
}

export function LoginSteps({ onComplete, onCancel: _onCancel }: LoginStepsProps): JSX.Element {
  const [phase, setPhase] = useState<Phase>('qr')
  const [dragOver, setDragOver] = useState(false)
  const inputRef = useRef<HTMLInputElement | null>(null)
  const photoLogin = usePhotoLogin()
  const sessionRef = useRef<Session | null>(null)

  useEffect(() => {
    if (phase !== 'welcome') return
    const t = window.setTimeout(() => {
      if (sessionRef.current) onComplete(sessionRef.current)
    }, 700)
    return () => window.clearTimeout(t)
  }, [phase, onComplete])

  const onPick = (e: ChangeEvent<HTMLInputElement>) => {
    const picked = e.target.files?.[0]
    if (!picked) return
    setPhase('pinging')
    photoLogin.mutate(picked, {
      onSuccess: ({ session }) => {
        sessionRef.current = session
        setPhase('welcome')
      },
      onError: () => setPhase('idle'),
    })
  }

  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setDragOver(false)
    const file = e.dataTransfer.files?.[0]
    if (!file) return
    setPhase('pinging')
    photoLogin.mutate(file, {
      onSuccess: ({ session }) => {
        sessionRef.current = session
        setPhase('welcome')
      },
      onError: () => setPhase('idle'),
    })
  }

  const onDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setDragOver(true)
  }

  const onDragLeave = () => setDragOver(false)

  // QR-side success handoff. The poll's `ready` payload already carries the
  // session, and the BE attaches a `Set-Cookie` to that same response — so by
  // the time we land here, the desktop is authenticated. Invalidating the
  // family/contracts/recs queries lets the scene flip on the next refetch.
  const onQrReady = useCallback((session: Session) => {
    sessionRef.current = session
    setPhase('welcome')
  }, [])

  return (
    <div className="w-full max-w-sm" data-testid="login-steps">
      <AnimatePresence mode="wait">
        {phase === 'qr' && (
          <motion.div
            key="qr"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.24, ease: [0, 0, 0, 1] }}
            className="pixel-card overflow-hidden"
          >
            <div className="bg-pop-silhouette text-pop-cream font-mono uppercase text-xs px-3 py-1 tracking-wider border-b-[3px] border-pop-silhouette">
              Přihlášení / Telefonem
            </div>
            <div className="flex flex-col items-center gap-5 p-6">
              <QrLoginStep
                onReady={onQrReady}
                onUseThisDevice={() => setPhase('idle')}
              />
            </div>
          </motion.div>
        )}

        {phase === 'idle' && (
          <motion.div
            key="idle"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.24, ease: [0, 0, 0, 1] }}
            className="pixel-card overflow-hidden"
          >
            <div className="bg-pop-silhouette text-pop-cream font-mono uppercase text-xs px-3 py-1 tracking-wider border-b-[3px] border-pop-silhouette">
              Přihlášení
            </div>
            <div className="flex flex-col items-center gap-5 p-6"
              onDrop={onDrop}
              onDragOver={onDragOver}
              onDragLeave={onDragLeave}
            >
              <h1 className="text-xl font-semibold uppercase tracking-wide text-pop-silhouette text-center">
                Nahraj fotku k přihlášení
              </h1>
              <p className="text-sm text-pop-shadow text-center">
                Pro demo stačí jakákoliv fotka — pozdravíme tě jménem.
              </p>
              <label
                htmlFor="photo-login-file"
                className={`pixel-input flex flex-col items-center gap-3 px-8 py-7 cursor-pointer transition-colors duration-75 ease-linear ${dragOver ? 'bg-pop-torch/30 border-pop-fire' : 'hover:bg-pop-sand-light'}`}
              >
                <div className="bg-pop-torch border-[3px] border-pop-silhouette p-2">
                  <Camera className="h-7 w-7 text-pop-silhouette" strokeWidth={2.5} />
                </div>
                <span className="text-sm font-semibold uppercase tracking-wide text-pop-silhouette">
                  {dragOver ? 'Pusť soubor zde' : 'Vyber nebo přetáhni soubor'}
                </span>
              </label>
              <input
                id="photo-login-file"
                ref={inputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={onPick}
                data-testid="photo-login-file"
              />
              <button
                onClick={() => setPhase('qr')}
                className="pixel-btn-secondary flex items-center gap-2 text-xs"
                data-testid="photo-login-back-to-qr"
              >
                <Smartphone className="h-3.5 w-3.5" strokeWidth={2.5} />
                Použít telefon
              </button>
            </div>
          </motion.div>
        )}

        {phase === 'pinging' && (
          <motion.div
            key="pinging"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.16, ease: 'linear' }}
            className="pixel-card flex items-center gap-3 px-5 py-4 font-mono uppercase text-sm text-pop-silhouette"
          >
            <Loader2 className="h-4 w-4 animate-spin text-pop-fire" strokeWidth={3} />
            <span>Pinging…</span>
          </motion.div>
        )}

        {phase === 'welcome' && (
          <motion.div
            key="welcome"
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.98 }}
            transition={{ duration: 0.2, ease: [0, 0, 0, 1] }}
            className="pixel-card flex flex-col items-center gap-3 px-6 py-8"
          >
            <div className="bg-pop-torch border-[3px] border-pop-silhouette p-2">
              <CheckCircle2 className="h-8 w-8 text-pop-silhouette" strokeWidth={2.5} />
            </div>
            <div className="text-2xl font-bold text-pop-torch text-center uppercase tracking-wide drop-shadow-[2px_2px_0_#1F1410]">
              Vítej, Anno!
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default LoginSteps

// ─── QR login step ─────────────────────────────────────────────────────────
/**
 * Issues an anonymous login-session token, renders a scannable QR pointing at
 * `/login/phone?token=…`, and polls `GET /api/auth/login-session/:token` until
 * the phone has logged in. The same response that flips `status` to `ready`
 * carries a `Set-Cookie` that authenticates the desktop, so we invalidate the
 * session-scoped queries before handing the session up to the parent.
 */
function QrLoginStep({
  onReady,
  onUseThisDevice,
}: {
  onReady: (session: Session) => void
  onUseThisDevice: () => void
}) {
  const qc = useQueryClient()
  const createSession = useCreateLoginSession()
  const mutate = createSession.mutate
  const [secondsLeft, setSecondsLeft] = useState<number | null>(null)

  useEffect(() => {
    mutate()
  }, [mutate])

  const session = createSession.data
  const token = session?.login_token ?? null
  const expiresAt = session?.expires_at
  const isExpired = secondsLeft !== null && secondsLeft <= 0

  useEffect(() => {
    if (!expiresAt) return
    const tick = () => {
      const diff = Math.round((new Date(expiresAt).getTime() - Date.now()) / 1000)
      setSecondsLeft(diff)
    }
    tick()
    const id = setInterval(tick, 1000)
    return () => clearInterval(id)
  }, [expiresAt])

  const statusPoll = useLoginSessionStatus(token, { enabled: !isExpired })

  useEffect(() => {
    const data = statusPoll.data
    if (data?.status !== 'ready') return
    qc.invalidateQueries({ queryKey: queryKeys.family })
    qc.invalidateQueries({ queryKey: queryKeys.contracts })
    qc.invalidateQueries({ queryKey: queryKeys.recommendations })
    onReady(data.session)
  }, [statusPoll.data, qc, onReady])

  // Desktop QR points at the FE phone-login page. We build the URL on the
  // client (window.location.origin) so dev (Vite proxy) and prod use whatever
  // origin the user actually opened.
  const phoneUrl =
    session && typeof window !== 'undefined'
      ? `${window.location.origin}/login/phone?token=${encodeURIComponent(session.login_token)}`
      : null

  return (
    <>
      <h1 className="text-xl font-semibold uppercase tracking-wide text-pop-silhouette text-center">
        Přihlas se telefonem
      </h1>
      <p className="text-sm text-pop-shadow text-center max-w-xs">
        Namiř telefon na QR kód a vyfoť se — pozdravíme tě jménem.
      </p>

      <div className="relative w-[208px] h-[208px] border-[3px] border-pop-silhouette bg-pop-cream p-3 flex items-center justify-center">
        {phoneUrl && !isExpired ? (
          <QRCodeSVG
            value={phoneUrl}
            size={184}
            bgColor="#F5E6C8"
            fgColor="#1F1410"
            level="M"
          />
        ) : isExpired ? (
          <div className="flex flex-col items-center gap-2 text-center text-pop-shadow">
            <RefreshCw className="h-8 w-8" strokeWidth={2.5} />
            <span className="text-xs font-mono uppercase">Kód vypršel</span>
          </div>
        ) : (
          <Loader2 className="h-8 w-8 animate-spin text-pop-fire" strokeWidth={3} />
        )}
      </div>

      <div className="flex items-center gap-2 text-xs font-mono uppercase text-pop-shadow min-h-[1.25rem]">
        {isExpired ? (
          <button
            onClick={() => mutate()}
            className="pixel-btn-secondary flex items-center gap-1 text-xs"
            data-testid="login-qr-refresh"
          >
            <RefreshCw className="h-3.5 w-3.5" strokeWidth={3} />
            Obnovit kód
          </button>
        ) : secondsLeft !== null ? (
          <>
            <Loader2 className="h-3.5 w-3.5 animate-spin text-pop-fire" strokeWidth={3} />
            <span>Čekám na telefon · {formatCountdown(secondsLeft)}</span>
          </>
        ) : null}
      </div>

      <button
        onClick={onUseThisDevice}
        className="pixel-btn-secondary flex items-center gap-2 text-xs"
        data-testid="login-qr-use-device"
      >
        <Camera className="h-3.5 w-3.5" strokeWidth={2.5} />
        Vyfotit na tomto zařízení
      </button>
    </>
  )
}

function formatCountdown(totalSeconds: number): string {
  const s = Math.max(0, totalSeconds)
  const m = Math.floor(s / 60)
  const rem = s % 60
  return `${m}:${rem.toString().padStart(2, '0')}`
}
